import * as React from 'react';
import { cn } from '../utils/cn';
import { Info, AlertTriangle, X as XIcon, CheckCircle, XCircle } from 'lucide-react';

export interface AlertProps {
  /** Alert message */
  message: React.ReactNode;
  /** Optional longer description */
  description?: React.ReactNode;
  /** Alert type */
  type?: 'success' | 'info' | 'warning' | 'error';
  /** Show icon */
  showIcon?: boolean;
  /** Show close button */
  closable?: boolean;
  /** Close callback */
  onClose?: () => void;
  /** Banner mode (full-width, no border) */
  banner?: boolean;
  /** Additional class names */
  className?: string;
  /** Inline style */
  style?: React.CSSProperties;
}

const typeStyles: Record<string, string> = {
  success: 'border-success/30 bg-success/10 text-success',
  info: 'border-info/30 bg-info/10 text-info',
  warning: 'border-warning/30 bg-warning/10 text-warning',
  error: 'border-destructive/30 bg-destructive/10 text-destructive',
};

const typeIcons: Record<string, React.ComponentType<any>> = {
  success: CheckCircle,
  info: Info,
  warning: AlertTriangle,
  error: XCircle,
};

export function Alert({
  message,
  description,
  type = 'info',
  showIcon = false,
  closable = false,
  onClose,
  banner = false,
  className,
  style,
}: AlertProps) {
  const [visible, setVisible] = React.useState(true);

  if (!visible) return null;

  const IconComp = typeIcons[type];

  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-2 border px-4 py-3 text-sm font-body',
        typeStyles[type],
        banner && 'border-x-0 rounded-none',
        className
      )}
      style={style}
    >
      {showIcon && IconComp && (
        <IconComp size={18} />
      )}
      <div className="flex-1">
        <div className="font-medium">{message}</div>
        {description && (
          <div className="mt-1 opacity-80">{description}</div>
        )}
      </div>
      {closable && (
        <button
          onClick={() => { setVisible(false); onClose?.(); }}
          className="!p-0 !h-auto !border-0 !bg-transparent opacity-60 hover:opacity-100 !tracking-normal !normal-case"
          aria-label="Stäng"
        >
          <XIcon size={16} />
        </button>
      )}
    </div>
  );
}
